// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=86-4853
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useState } from "react";
import { setFunnelModalConfig } from "../../../components/funnel/FunnelModal";
import { FUNNEL_TYPES } from "../../../components/funnel/utils/contants";
import { FunnelContextArea } from "../../../components/funnel/types";

type HoverTarget = "reader" | "publish" | null;

export function SignupSection() {
  const [hovered, setHovered] = useState<HoverTarget>(null);
  const glowColor =
    hovered === "reader"
      ? "rgba(255, 90, 44, 0.25)"
      : hovered === "publish"
        ? "rgba(255, 255, 255, 0.14)"
        : "transparent";

  return (
    <Box maxW="960px" mx="auto" px="24px" pt="40px" pb="64px">
      <Flex
        bg="charcoal.600"
        border="1px solid rgba(255,255,255,0.08)"
        borderRadius="24px"
        direction="column"
        align="center"
        gap="40px"
        px={{ base: "24px", md: "64px", lg: "175px" }}
        py="56px"
        textAlign="center"
        position="relative"
        overflow="hidden"
      >
        {/* z:0 — Bottom glow */}
        <Box
          position="absolute"
          bottom={hovered ? "-42%" : "-55%"}
          left="50%"
          transform="translateX(-50%)"
          w="90%"
          h="70%"
          borderRadius="full"
          bg={glowColor}
          filter="blur(60px)"
          transition="background 0.5s ease, bottom 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)"
          pointerEvents="none"
          zIndex={0}
        />

        {/* z:1 — Stripe texture */}
        <Box
          position="absolute"
          inset="0"
          pointerEvents="none"
          zIndex={1}
          backgroundImage={`repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 1px,
            #101112 1px,
            #101112 9px
          )`}
          sx={{
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, white 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, white 100%)",
          }}
        />

        {/* z:2 — Vignette over stripes/glow for depth */}
        <Box
          position="absolute"
          inset="0"
          borderRadius="24px"
          pointerEvents="none"
          zIndex={2}
          bg="linear-gradient(to top, #101112 0%, transparent 10%)"
        />

        {/* Heading + subtitle */}
        <Flex direction="column" gap="8px" align="center" position="relative" zIndex={3}>
          <Text
            fontFamily="'Bricolage Grotesque', sans-serif"
            fontSize={{ base: "28px", md: "40px" }}
            fontWeight="600"
            color="dune.dune"
            letterSpacing="-0.8px"
          >
            Ready to start your journey?
          </Text>
          <Text
            fontSize="20px"
            fontWeight="400"
            color="dune.700"
            lineHeight="1.4"
            maxW="600px"
          >
            Whether you are looking to publish your next masterpiece or discover
            your new favorite series, there's a place for you here.
          </Text>
        </Flex>

        {/* Buttons */}
        <Flex
          gap="16px"
          direction={{ base: "column", md: "row" }}
          w={{ base: "100%", md: "auto" }}
          position="relative"
          zIndex={3}
        >
          <Button
            variant="primary"
            size="lg"
            w={{ base: "100%", md: "230px" }}
            onMouseEnter={() => setHovered("reader")}
            onMouseLeave={() => setHovered(null)}
            onClick={() =>
              setFunnelModalConfig({
                type: FUNNEL_TYPES.signup,
                context: {
                  modalContext: FunnelContextArea.aboutPage,
                },
              })
            }
          >
            Join as reader
          </Button>
          <Button
            as="a"
            href="https://globalcomix.com/publish"
            variant="secondary"
            size="lg"
            w={{ base: "100%", md: "230px" }}
            onMouseEnter={() => setHovered("publish")}
            onMouseLeave={() => setHovered(null)}
          >
            Publish your work
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
